/**
 * AdminStates — shared loading / error / empty wrapper for the P0 admin kit
 * (charts, KPI cards, gauges). Mirrors the four-state contract the shipped
 * `components/AdminTable.tsx` inlines, factored out so every kit surface is
 * consistent. `--g-*` tokens only. Error takes precedence over empty.
 */
import { type ReactElement, type ReactNode } from "react";
import { Skeleton } from "@garden/ui";

export interface AdminStatesProps {
  isLoading: boolean;
  isError: boolean;
  isEmpty: boolean;
  loadingLabel: string;
  emptyLabel: string;
  errorLabel: string;
  testid: string;
  /** Skeleton rows while loading. Defaults to 3. */
  loadingRows?: number;
  children: ReactNode;
}

export function AdminStates({
  isLoading,
  isError,
  isEmpty,
  loadingLabel,
  emptyLabel,
  errorLabel,
  testid,
  loadingRows = 3,
  children,
}: AdminStatesProps): ReactElement {
  if (isLoading) {
    return (
      <div
        data-testid={`${testid}-loading`}
        role="status"
        aria-busy="true"
        aria-label={loadingLabel}
        className="flex flex-col gap-2"
      >
        {Array.from({ length: loadingRows }).map((_, i) => (
          <Skeleton key={i} className="h-10 w-full rounded-[var(--g-radius-md)]" />
        ))}
      </div>
    );
  }
  if (isError) {
    return (
      <p
        data-testid={`${testid}-error`}
        role="alert"
        className="py-6 text-sm text-[var(--g-error)]"
      >
        {errorLabel}
      </p>
    );
  }
  if (isEmpty) {
    return (
      <p
        data-testid={`${testid}-empty`}
        className="py-8 text-center text-sm text-[var(--g-secondary)]"
      >
        {emptyLabel}
      </p>
    );
  }
  return <>{children}</>;
}

export default AdminStates;
